import type {
  ApiResponse,
  CallSession,
  CallTariffSummaryItem,
  PaginatedResponse,
  RateCallPayload,
  ResumableCallSession,
  TranscriptionStatus,
} from '@klaro7/shared-types';

import api from '@/lib/api';

export interface CallHistoryResponse extends PaginatedResponse<CallSession> {
  /** Bonus/promo/standard usage breakdown across the whole filtered history
   * (not just the current page); absent for the unscoped "all activities" view. */
  tariffSummary?: CallTariffSummaryItem[];
}

export async function getCallHistory(params?: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  /** Admin-only: list the calls made by a specific user */
  callerId?: string;
  /** Admin: list the calls a specific professional received */
  professionalId?: string;
}): Promise<CallHistoryResponse> {
  const response = await api.get<CallHistoryResponse>('/calls', { params });
  return response.data;
}

export async function getCallSession(id: string): Promise<CallSession> {
  const response = await api.get<ApiResponse<CallSession>>(`/calls/${id}`);
  return response.data.data;
}

export async function getActiveCall(): Promise<ResumableCallSession | null> {
  const response = await api.get<ApiResponse<ResumableCallSession | null>>('/calls/active');
  return response.data.data;
}

export async function rateCall(id: string, data: RateCallPayload): Promise<CallSession> {
  const response = await api.post<ApiResponse<CallSession>>(`/calls/${id}/rate`, data);
  return response.data.data;
}

export async function uploadRecording(
  id: string,
  blob: Blob,
  fileName = 'recording.webm',
): Promise<void> {
  const formData = new FormData();
  formData.append('recording', blob, fileName);
  await api.post(`/calls/${id}/recording`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/**
 * Upload incrementale dei byte registrati durante la call (piano B se la
 * pagina muore prima dell'upload finale). Ritorna il size totale ora sul
 * server; su 409 (offset disallineato: retry doppio o part perso lato server)
 * ritorna il size comunicato dal server così il recorder si riallinea.
 */
export async function uploadRecordingChunk(
  id: string,
  chunk: Blob,
  offset: number,
  fileName: string,
): Promise<number> {
  const ext = fileName.split('.').pop() || 'webm';
  try {
    const response = await api.post<ApiResponse<{ size: number }>>(
      `/calls/${id}/recording-chunk`,
      chunk,
      {
        params: { offset, ext },
        headers: { 'Content-Type': 'application/octet-stream' },
      },
    );
    return response.data.data.size;
  } catch (err) {
    const conflict = err as { response?: { status?: number; data?: { data?: { currentSize?: number } } } };
    if (conflict.response?.status === 409 && typeof conflict.response.data?.data?.currentSize === 'number') {
      return conflict.response.data.data.currentSize;
    }
    throw err;
  }
}

export async function getTranscription(id: string): Promise<{
  transcription: string | null;
  transcriptionStatus: TranscriptionStatus;
}> {
  const response = await api.get<ApiResponse<{
    transcription: string | null;
    transcriptionStatus: TranscriptionStatus;
  }>>(`/calls/${id}/transcription`);
  return response.data.data;
}
